import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ChevronsUpDown,
  Clock3,
  Coins,
  Euro,
  Loader2,
  MapPin,
  Percent,
  Receipt,
  RefreshCw,
  Timer,
} from 'lucide-react'
import { ApiError, fetchStores, type StoreOption } from '../api/auth'
import { fetchGeneralOverview, type OverviewStore } from '../api/overview'
import { sortOverviewStores, type OverviewSort, type OverviewSortKey } from '../lib/overviewSort'
import { ShopPills } from './ShopPills'
import { SalesShareChart } from './SalesShareChart'
import { GridAdherenceChart } from './GridAdherenceChart'
import { PeriodControls } from './PeriodControls'
import { hasCapability, useAuth } from '../context/AuthContext'
import { canSelectShops, entitledStores } from '../lib/access'
import { formatCurrency, formatNumber, formatPercent, formatShortTimestamp } from '../lib/format'
import { SHOP_REGIONS } from '../dashboard/regions'
import {
  formatRange,
  isSingleDay,
  isoDay,
  matchingPreset,
  rangePresets,
  type DateRange,
} from '../lib/ranges'

const PRESET_BASE =
  'rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors'
const PRESET_ACTIVE =
  'border-salsa-500 bg-salsa-500/10 text-salsa-700 dark:border-salsa-500/50 dark:text-salsa-300'
const PRESET_IDLE =
  'border-slate-200 text-slate-600 hover:border-salsa-300 hover:text-salsa-700 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200'

/** The table headers, offered as a select on a phone (D-106). */
const MOBILE_SORT_OPTIONS: { key: OverviewSortKey; label: string }[] = [
  { key: 'shop', label: 'Shop number' },
  { key: 'sales', label: 'Sales (period)' },
  { key: 'today', label: 'Sales today' },
  { key: 'grid', label: 'Grid hours' },
  { key: 'worked', label: 'Worked hours' },
  { key: 'diff', label: 'Worked vs grid' },
]

interface KpiDelta {
  text: string
  tone: 'good' | 'bad' | 'neutral'
  title?: string
}

interface KpiCardProps {
  label: string
  value: string
  hint: string
  source: string
  icon: ComponentType<{ className?: string }>
  accent: string
  busy?: boolean
  delta?: KpiDelta
}

const DELTA_TONE: Record<KpiDelta['tone'], string> = {
  good: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  bad: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  neutral: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
}

/**
 * KPI grid per number of visible cards (D-109).
 *
 * Written out rather than interpolated because Tailwind only ships the classes it
 * can see in source. Six cards read as 2 × 3 on a phone, 3 × 2 on a tablet and one
 * clean row on a large desk; fewer cards keep the wider slots they had before.
 */
const KPI_GRID_BY_COUNT: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 xl:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-5',
  6: 'grid-cols-2 sm:grid-cols-3 xl:grid-cols-6',
}

function formatSignedHours(value: number): string {
  const sign = value < 0 ? '−' : value > 0 ? '+' : ''
  return `${sign}${formatNumber(Math.abs(value), 1)} h`
}

/** One live figure and, optionally, a coloured comparison badge. */
function KpiCard({
  label,
  value,
  hint,
  source,
  icon: Icon,
  accent,
  busy,
  delta,
}: KpiCardProps) {
  return (
    <article className="card flex flex-col gap-1.5 p-3 sm:gap-2 sm:p-4" title={source} aria-busy={busy}>
      <div className="flex items-start justify-between gap-2">
        <span className="kpi-label">{label}</span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg sm:h-8 sm:w-8 sm:rounded-xl ${accent}`}
          aria-hidden="true"
        >
          <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </span>
      </div>
      <span className="flex items-center gap-2 text-lg font-semibold tabular-nums tracking-tight text-slate-900 sm:text-xl dark:text-white">
        {busy && (
          <Loader2
            className="h-3.5 w-3.5 shrink-0 animate-spin text-slate-400 dark:text-slate-500"
            aria-hidden="true"
          />
        )}
        {value}
      </span>
      {delta && (
        <span className={`badge w-fit ${DELTA_TONE[delta.tone]}`} title={delta.title}>
          {delta.text}
        </span>
      )}
      <span className="text-[11px] leading-snug text-slate-500 dark:text-slate-400">{hint}</span>
      {/* The source line is provenance for the desk, not for a phone screen. */}
      <span className="mt-auto hidden text-[10px] leading-snug text-slate-400 sm:block dark:text-slate-500">
        {source}
      </span>
    </article>
  )
}

function shopStatus(row: OverviewStore): { label: string; tone: string; title?: string } {
  if (row.error_code === 'no_sales_data_in_period') {
    return {
      label: 'No sales data',
      tone: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
      title:
        row.error_message ??
        'Neither the VM Hub archive nor Butlaroo has sales for this shop in this period.',
    }
  }
  if (!row.revenue_available) {
    return {
      label: row.error_code === 'butlaroo_credentials_missing' ? 'No sales feed' : 'Unavailable',
      tone: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
      title: row.error_message ?? undefined,
    }
  }
  if (row.grid_hours === null) {
    return {
      label: 'Sales only',
      tone: 'bg-slate-500/15 text-slate-600 dark:text-slate-300',
      title: row.error_message ?? undefined,
    }
  }
  return { label: 'Live', tone: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' }
}

interface SortableHeaderProps {
  label: string
  sortKey: OverviewSortKey
  sort: OverviewSort
  onSort: (key: OverviewSortKey) => void
  title: string
}

/** One sortable table heading with the shared arrow / neutral indicator. */
function SortableHeader({ label, sortKey, sort, onSort, title }: SortableHeaderProps) {
  const direction = sort?.key === sortKey ? sort.direction : null
  return (
    <th
      className="px-4 py-2 font-semibold"
      aria-sort={
        direction === 'asc' ? 'ascending' : direction === 'desc' ? 'descending' : 'none'
      }
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={title}
        className="inline-flex items-center gap-1 font-semibold transition-colors hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-salsa-500/50 dark:hover:text-white"
      >
        {label}
        {direction === 'desc' ? (
          <ArrowDown className="h-3 w-3" aria-hidden="true" />
        ) : direction === 'asc' ? (
          <ArrowUp className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronsUpDown className="h-3 w-3 opacity-50" aria-hidden="true" />
        )}
      </button>
    </th>
  )
}

/**
 * One shop as a phone card (D-106).
 *
 * Same figures as the table row, laid out as label/value pairs so nothing needs a
 * sideways scroll to be read. Missing figures stay “—” and keep their reason in the
 * `title`, exactly like the table.
 */
function ShopCard({ row, showWorked }: { row: OverviewStore; showWorked: boolean }) {
  const status = shopStatus(row)
  const gridDiff =
    row.worked_hours != null && row.grid_hours != null ? row.worked_hours - row.grid_hours : null
  const workedLabel =
    row.worked_hours != null
      ? `${formatNumber(row.worked_hours, 1)} h`
      : row.hours_error_code === 'shiftbase_store_not_configured'
        ? 'No mapping'
        : row.hours_error_code
          ? 'Unavailable'
          : '—'

  return (
    <li className="space-y-1.5 px-3 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 text-xs font-semibold text-slate-800 dark:text-slate-100">
          <span className="font-mono text-[10px] font-normal text-slate-400 dark:text-slate-500">
            {row.store_code} ·{' '}
          </span>
          {row.store_name}
        </p>
        <span className={`badge shrink-0 ${status.tone}`} title={status.title}>
          {status.label}
        </span>
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-slate-500 dark:text-slate-400">Sales</dt>
          <dd className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
            {row.sales_period_eur != null ? formatCurrency(row.sales_period_eur) : '—'}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-slate-500 dark:text-slate-400">Today</dt>
          <dd className="font-semibold tabular-nums text-slate-800 dark:text-slate-100">
            {row.sales_today_eur != null ? formatCurrency(row.sales_today_eur) : '—'}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-slate-500 dark:text-slate-400">Grid</dt>
          <dd className="tabular-nums text-slate-700 dark:text-slate-200">
            {row.grid_hours != null ? `${formatNumber(row.grid_hours, 1)} h` : '—'}
          </dd>
        </div>
        {showWorked && (
          <div className="flex items-baseline justify-between gap-2">
            <dt className="text-slate-500 dark:text-slate-400">Worked</dt>
            <dd
              className="tabular-nums text-slate-700 dark:text-slate-200"
              title={row.hours_error_message ?? undefined}
            >
              {workedLabel}
            </dd>
          </div>
        )}
        {showWorked && gridDiff != null && (
          <div className="col-span-2 flex items-baseline justify-between gap-2">
            <dt className="text-slate-500 dark:text-slate-400">Worked vs grid</dt>
            <dd>
              <span
                className={`badge ${
                  gridDiff < 0
                    ? DELTA_TONE.good
                    : gridDiff > 0
                      ? DELTA_TONE.bad
                      : DELTA_TONE.neutral
                }`}
              >
                {formatSignedHours(gridDiff)}
              </span>
            </dd>
          </div>
        )}
      </dl>
    </li>
  )
}

/**
 * S-01 · General Overview — the default landing view, now live.
 *
 * Defaults (D-60): **This week** and **every shop the account is entitled to**.
 * It shows live sales for the period, sales today and COL grid hours — and
 * nothing else. Forecast, COL% and COS% were removed from this dashboard for
 * good; grid hours come from the COL workbook and worked hours from Shiftbase
 * (timesheets for completed days, elapsed roster today).
 * Shiftbase (assistant manager and above).
 */
export function GeneralOverviewPanel() {
  const { user } = useAuth()
  const now = useMemo(() => new Date(), [])
  // "All time" is longer than the vendor's 366-day guard; it is not offered here.
  const presets = useMemo(
    () => rangePresets(now).filter((preset) => preset.label !== 'All time'),
    [now],
  )
  const [range, setRange] = useState<DateRange>(() => {
    const week = rangePresets(new Date()).find((preset) => preset.label === 'This week')
    return week
      ? { from: week.from, to: week.to, preset: 'This week' }
      : { from: presets[0].from, to: presets[0].to }
  })
  const [pickedIds, setPickedIds] = useState<number[] | null>(null)
  const [sort, setSort] = useState<OverviewSort>(null)
  const [live, setLive] = useState(true)
  const [tick, setTick] = useState(() => Date.now())
  const [manualRefreshAt, setManualRefreshAt] = useState(0)

  // Tick once a second only while Live is on; the countdown is derived from the
  // query's last successful update, so a silent refresh resets it naturally.
  useEffect(() => {
    if (!live) return
    setTick(Date.now())
    const timer = window.setInterval(() => setTick(Date.now()), 1_000)
    return () => window.clearInterval(timer)
  }, [live])

  const storesQuery = useQuery({
    queryKey: ['stores'],
    queryFn: fetchStores,
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  })
  const catalogue: StoreOption[] = storesQuery.data ?? []
  const visibleStores = useMemo(() => entitledStores(user, catalogue), [user, catalogue])
  const visibleIds = useMemo(() => visibleStores.map((store) => store.id), [visibleStores])
  const regionOptions = useMemo(
    () =>
      SHOP_REGIONS.map((region) => {
        const codes = new Set(region.storeCodes)
        return {
          ...region,
          ids: visibleStores
            .filter((store) => codes.has(store.code))
            .map((store) => store.id),
        }
      }),
    [visibleStores],
  )
  const selectedIds = useMemo(() => {
    const base = pickedIds ?? visibleIds
    const allowed = new Set(visibleIds)
    return base.filter((id) => allowed.has(id))
  }, [pickedIds, visibleIds])
  const allSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length
  const noneSelected = selectedIds.length === 0

  const canPick = canSelectShops(user)
  // A district manager already only has their district in scope; the other
  // regional shortcuts would just render disabled. Keep All/None only.
  const showRegionQuickSelect = canPick && user?.role !== 'district_manager'
  const ownStoreId = user?.store_id ?? null
  // Single-store roles have no pills; their own store is the scope even if the
  // catalogue request failed.
  const selected =
    selectedIds.length > 0
      ? selectedIds
      : !canPick && ownStoreId !== null
        ? [ownStoreId]
        : []
  const activePreset = matchingPreset(range, now)
  const periodLabel =
    activePreset === 'This week' ? 'Week to Date' : activePreset ?? 'Selected Period'

  const overviewQuery = useQuery({
    queryKey: ['overview', range.from, range.to, selected.join(',')],
    queryFn: ({ signal }) =>
      fetchGeneralOverview({ from: range.from, to: range.to, storeIds: selected, signal }),
    enabled: Boolean(user) && selected.length > 0,
    staleTime: 30_000,
    refetchInterval: live ? 30_000 : false,
    retry: 1,
  })
  const data = overviewQuery.data
  const canSeePeriodSales = hasCapability(user, 'overview.sales_period')
  const canSeeGridHours = hasCapability(user, 'overview.grid_hours')
  const canSeeWorkedHours = hasCapability(user, 'overview.worked_hours')
  const canSeeColPct = hasCapability(user, 'overview.col_pct')
  const canSeeCosPct = hasCapability(user, 'overview.cos_pct')
  // Keep KPI slots (and their spinners) stable while loading. The API's feature
  // flags are authoritative once data arrives; before then the backend-issued
  // capabilities decide, not a duplicated role list.
  const showPeriod = data?.features.period_sales ?? canSeePeriodSales
  const showGrid = data?.features.grid_hours ?? canSeeGridHours
  const showWorked = data?.features.worked_hours ?? canSeeWorkedHours
  const showCol = data?.features.col_pct ?? canSeeColPct
  const showCos = data?.features.cos_pct ?? canSeeCosPct
  // When the selected period is a single trading day that is *today*, the period
  // total and “Sales Today” are the same number. Show one card, not two (D-110):
  // “Sales Today” stays (every role has it) and the period card steps aside.
  const periodIsToday = data
    ? isSingleDay({ from: data.start_date, to: data.end_date }, data.today)
    : isSingleDay(range, isoDay(now))
  const showPeriodCard = showPeriod && !periodIsToday
  const adherenceStores =
    data?.stores.filter(
      (store) =>
        store.worked_hours != null &&
        store.grid_hours != null &&
        store.grid_hours > 0,
    ).length ?? 0
  const showAdherence = showWorked && adherenceStores >= 2
  // A one-shop view has no “share by shop” story; hide the donut entirely, just
  // as the adherence chart is hidden when there is no ranking to draw.
  const showSalesShare = selected.length > 1
  const includesToday = data
    ? data.start_date <= data.today && data.today <= data.end_date
    : false
  const kpiCount =
    (showPeriodCard ? 1 : 0) +
    1 +
    (showGrid ? 1 : 0) +
    (showWorked ? 1 : 0) +
    (showCol ? 1 : 0) +
    (showCos ? 1 : 0)
  // Two KPIs per row on a phone: six full-width cards would push the shop list — the
  // reason people open this screen — far below the fold (D-106, D-109).
  const kpiGridClass = KPI_GRID_BY_COUNT[kpiCount] ?? 'grid-cols-2'
  const gridDelta: KpiDelta | undefined =
    data?.features.worked_hours && data.totals.grid_variance != null
      ? {
          text:
            `${formatSignedHours(data.totals.grid_variance)} vs grid ` +
            `(${data.totals.variance_stores} shop${
              data.totals.variance_stores === 1 ? '' : 's'
            })`,
          tone:
            data.totals.grid_variance < 0
              ? 'good'
              : data.totals.grid_variance > 0
                ? 'bad'
                : 'neutral',
          title:
            `Worked minus grid across ${data.totals.variance_stores} shop` +
            `${data.totals.variance_stores === 1 ? '' : 's'} with both figures. ` +
            'Negative (green) is below grid; positive (red) is above grid.' +
            (includesToday ? ' Worked hours for today are to date.' : ''),
        }
      : undefined
  const busy = overviewQuery.isPending && selected.length > 0
  const refreshAnchor = Math.max(overviewQuery.dataUpdatedAt, manualRefreshAt)
  const secondsUntilRefresh =
    live && refreshAnchor > 0
      ? Math.max(
          0,
          Math.ceil((refreshAnchor + 30_000 - tick) / 1_000),
        )
      : null
  const sortedStores = useMemo(
    () => sortOverviewStores(data?.stores ?? [], sort),
    [data, sort],
  )
  const toggleSort = (key: OverviewSortKey) => {
    setSort((current) => {
      if (!current || current.key !== key) {
        // Shop numbers read naturally ascending; numeric metrics read best highest-first.
        return { key, direction: key === 'shop' ? 'asc' : 'desc' }
      }
      return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
    })
  }
  /** Phone sort control: pick a metric with the same default direction as the headers. */
  const chooseSortKey = (key: OverviewSortKey) => {
    setSort({ key, direction: key === 'shop' ? 'asc' : 'desc' })
  }
  const errorMessage =
    overviewQuery.error instanceof ApiError
      ? overviewQuery.error.message
      : overviewQuery.error
        ? 'Live sales are unavailable right now.'
        : null

  const toggleStore = (id: number) => {
    setPickedIds((current) => {
      const base = current ?? visibleIds
      return base.includes(id) ? base.filter((value) => value !== id) : [...base, id]
    })
  }
  const selectAll = () => setPickedIds(visibleIds)
  const clearSelection = () => setPickedIds([])

  const toggleLive = () => {
    const next = !live
    setLive(next)
    // Turning Live on should not wait up to 30 s for the first silent update.
    if (next) {
      setManualRefreshAt(Date.now())
      setTick(Date.now())
      void overviewQuery.refetch()
    }
  }
  const refreshNow = () => {
    // Works in both modes; the countdown starts again from this press.
    setManualRefreshAt(Date.now())
    setTick(Date.now())
    void overviewQuery.refetch()
  }

  return (
    <section aria-label="General overview" className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white">
            General Overview
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {showPeriod || showGrid || showWorked
              ? 'Live sales and labour hours for the shops you may see.'
              : 'Live sales for today for the shops you may see.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={live}
            aria-label="Live updates every 30 seconds"
            title={
              live
                ? 'Live updates every 30 seconds — switch off to refresh manually'
                : 'Switch on for silent updates every 30 seconds'
            }
            onClick={toggleLive}
            disabled={!user}
            className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-slate-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:text-slate-300 dark:hover:border-white/20"
          >
            <span
              className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                live ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'
              }`}
              aria-hidden="true"
            >
              <span
                className={`inline-block h-3 w-3 rounded-full bg-white shadow-sm transition-transform ${
                  live ? 'translate-x-3.5' : 'translate-x-0.5'
                }`}
              />
            </span>
            <span className={live ? 'text-emerald-700 dark:text-emerald-300' : ''}>Live</span>
          </button>

          {secondsUntilRefresh !== null && (
            <span
              className="inline-flex min-w-[1.9rem] items-center justify-end text-[10px] font-semibold tabular-nums text-slate-400 dark:text-slate-500"
              title="Time until the next silent refresh"
            >
              {overviewQuery.isFetching ? (
                <Loader2
                  className="h-2.5 w-2.5 animate-spin"
                  aria-label="Refreshing"
                  role="status"
                />
              ) : (
                `${secondsUntilRefresh}s`
              )}
            </span>
          )}

          {data && (
            <span className="text-[11px] text-slate-400 dark:text-slate-500">
              updated {formatShortTimestamp(data.fetched_at)}
            </span>
          )}

          <button
            type="button"
            onClick={refreshNow}
            disabled={!user || selected.length === 0 || overviewQuery.isFetching}
            title="Refresh now — resets the Live timer"
            aria-label="Refresh sales now"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-salsa-300 hover:text-salsa-700 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-slate-300 dark:hover:border-salsa-500/40 dark:hover:text-salsa-200"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${overviewQuery.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </div>

      <section className="card space-y-3 p-3 sm:p-4">
        {showPeriod && (
          <PeriodControls value={range} onChange={setRange} presets={presets} now={now} />
        )}

        {canPick && (
          // One sideways rail on a phone (D-106): six region pills would otherwise
          // wrap into three rows above the numbers people opened the screen for.
          <div className="rail-x flex flex-nowrap items-center gap-1.5 pb-1 sm:flex-wrap sm:overflow-visible sm:pb-0">
            <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Region
            </span>
            <button
              type="button"
              onClick={selectAll}
              disabled={visibleIds.length === 0}
              aria-pressed={allSelected}
              className={`${PRESET_BASE} ${allSelected ? PRESET_ACTIVE : PRESET_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              All
            </button>
            <button
              type="button"
              onClick={clearSelection}
              disabled={noneSelected}
              aria-pressed={noneSelected}
              className={`${PRESET_BASE} ${noneSelected ? PRESET_ACTIVE : PRESET_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
            >
              None
            </button>
            {showRegionQuickSelect &&
              regionOptions.map((region) => {
                const active =
                  region.ids.length > 0 &&
                  region.ids.length === selectedIds.length &&
                  region.ids.every((id) => selectedIds.includes(id))
                return (
                  <button
                    key={region.key}
                    type="button"
                    onClick={() => setPickedIds(region.ids)}
                    disabled={region.ids.length === 0}
                    aria-pressed={active}
                    title={`${region.name} · ${region.ids.length} shops (${region.storeCodes.join(', ')})`}
                    className={`${PRESET_BASE} ${active ? PRESET_ACTIVE : PRESET_IDLE} disabled:cursor-not-allowed disabled:opacity-40`}
                  >
                    {region.name}
                  </button>
                )
              })}
          </div>
        )}

        {canPick ? (
          <ShopPills
            stores={visibleStores}
            selectedIds={selectedIds}
            onToggle={toggleStore}
            onSelectAll={selectAll}
            onClear={clearSelection}
            showControls={false}
          />
        ) : (
          <p className="flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
            <MapPin className="h-3 w-3" aria-hidden="true" />
            {visibleStores[0]?.label ?? `Store #${String(ownStoreId ?? '').padStart(3, '0')}`}
          </p>
        )}

        {storesQuery.isError && (
          <p className="flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300">
            <AlertTriangle className="h-3 w-3" aria-hidden="true" />
            Could not load the shop list.
            <button
              type="button"
              onClick={() => void storesQuery.refetch()}
              className="font-semibold underline underline-offset-2"
            >
              Retry
            </button>
          </p>
        )}
      </section>

      <div className={`grid gap-2 sm:gap-4 ${kpiGridClass}`}>
        {showPeriodCard && (
          <KpiCard
            label={`Live Sales (${periodLabel})`}
            value={
              data?.totals.sales_period_eur != null
                ? formatCurrency(data.totals.sales_period_eur)
                : '—'
            }
            hint={
              data
                ? data.stores_with_sales === data.stores_requested
                  ? `All ${data.stores_requested} shops reporting`
                  : `${data.stores_with_sales} of ${data.stores_requested} shops reporting`
                : 'Butlaroo Insights'
            }
            source="Butlaroo Insights · vendor aggregate, ex-VAT"
            icon={Euro}
            accent="bg-sky-500/15 text-sky-600 dark:text-sky-400"
            busy={busy}
          />
        )}
        <KpiCard
          label="Sales Today"
          value={data ? formatCurrency(data.totals.sales_today_eur) : '—'}
          hint={
            data
              ? periodIsToday
                ? // The period card is hidden on a single-day “today”, so this card
                  // carries the shops-reporting hint instead of repeating the date.
                  data.stores_with_sales === data.stores_requested
                  ? `All ${data.stores_requested} shops reporting`
                  : `${data.stores_with_sales} of ${data.stores_requested} shops reporting`
                : formatRange({ from: data.today, to: data.today })
              : 'Live'
          }
          source="Butlaroo Insights · business day"
          icon={Receipt}
          accent="bg-mango-500/15 text-mango-600 dark:text-mango-400"
          busy={busy}
        />
        {showGrid && (
          <KpiCard
            label="Grid Hours"
            value={
              data?.totals.grid_hours != null
                ? `${formatNumber(data.totals.grid_hours, 1)} h`
                : '—'
            }
            hint={
              data
                ? `Grid for the period · ${data.stores_with_sales} shop${
                    data.stores_with_sales === 1 ? '' : 's'
                  }`
                : 'Labor grid'
            }
            source="Labor grid"
            icon={Clock3}
            accent="bg-amber-500/15 text-amber-600 dark:text-amber-400"
            delta={gridDelta}
            busy={busy}
          />
        )}
        {showWorked && (
          <KpiCard
            label="Worked Hours"
            value={
              data?.totals.worked_hours != null
                ? `${formatNumber(data.totals.worked_hours, 1)} h`
                : '—'
            }
            hint={
              data
                ? `Shiftbase actual · ${data.totals.hours_stores} shop${
                    data.totals.hours_stores === 1 ? '' : 's'
                  } reporting${
                    data.totals.hours_stores > data.totals.variance_stores
                      ? ` · ${data.totals.variance_stores} with grid`
                      : ''
                  }${includesToday ? ' · today to date' : ''}`
                : 'Shiftbase · timesheets + roster today'
            }
            source="Shiftbase · timesheets + elapsed roster today"
            icon={Timer}
            accent="bg-violet-500/15 text-violet-600 dark:text-violet-400"
            busy={busy}
          />
        )}
        {showCol && (
          <KpiCard
            label="COL%"
            value={data?.totals.col_pct != null ? formatPercent(data.totals.col_pct, 1) : '—'}
            hint={
              data
                ? data.totals.col_eur != null
                  ? `${formatCurrency(data.totals.col_eur)} labour · ${data.totals.col_stores} shop${
                      data.totals.col_stores === 1 ? '' : 's'
                    } with cost + sales`
                  : 'Cost or sales missing for every shop in scope'
                : 'Shiftbase · timesheets + elapsed roster today'
            }
            source="Cost of labour ÷ sales · Shiftbase + Butlaroo · identical to 4-Wall Analysis"
            icon={Percent}
            accent="bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
            busy={busy}
          />
        )}
        {showCos && (
          <KpiCard
            label="COS%"
            value="X"
            hint="Cost of sales is not connected yet — no invoice or recipe cost feed."
            source="Placeholder · shows X on purpose instead of a made-up number"
            icon={Coins}
            accent="bg-slate-500/15 text-slate-500 dark:text-slate-400"
            delta={{
              text: 'Not live yet',
              tone: 'neutral',
              title:
                'Cost of sales needs invoice (Basecone) or recipe (Horeko) cost data. ' +
                'Until that feed exists this card stays a placeholder rather than guess.',
            }}
          />
        )}

      </div>

      {data && !showPeriod && (
        <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
          Your role shows today&apos;s sales. Period sales and grid hours start at Assistant
          Manager.
        </p>
      )}

      {data && showPeriod && !showWorked && (
        <p className="text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">
          Actual worked hours and the grid variance start at Assistant Manager.
        </p>
      )}

      {data && showPeriod && (showAdherence || showSalesShare) && (
        showAdherence ? (
          <div className="grid gap-4 xl:grid-cols-2">
            <SalesShareChart stores={data.stores} requested={data.stores_requested} />
            <GridAdherenceChart
              stores={data.stores}
              requested={data.stores_requested}
            />
          </div>
        ) : (
          <SalesShareChart stores={data.stores} requested={data.stores_requested} />
        )
      )}

      {selected.length === 0 && user && (
        <section className="card space-y-2 p-4 text-center">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            Choose at least one shop
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            All shops in your scope are selected by default. Pick one above to bring the
            live figures back.
          </p>
          {visibleStores.length > 0 && (
            <button
              type="button"
              onClick={selectAll}
              className="inline-flex items-center gap-1.5 rounded-xl bg-salsa-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-salsa-600"
            >
              Select all {visibleStores.length} shops
            </button>
          )}
        </section>
      )}

      {user && !canPick && visibleStores.length === 0 && storesQuery.isSuccess && (
        <p className="text-[11px] text-amber-700 dark:text-amber-300">
          No shop is linked to this account.
        </p>
      )}

      {busy && !data && (
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Loading live figures…
        </p>
      )}

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-xs text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            <strong>Live sales are unavailable.</strong> {errorMessage}
          </span>
          <button
            type="button"
            onClick={() => void overviewQuery.refetch()}
            className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold hover:bg-amber-500/10"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${overviewQuery.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Retry
          </button>
        </div>
      )}


      {data && showPeriod && data.stores.length > 1 && (
        <section className="card overflow-hidden">
          <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-3 py-2.5 sm:px-4 sm:py-3 dark:border-white/10">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
              Shop performance
            </h3>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {data.stores_with_sales} of {data.stores_requested} shops with sales
              {showWorked ? ` · ${data.totals.hours_stores} with Shiftbase` : ''}
            </span>
          </header>

          {/* Phones get a card per shop plus the same sort options as the headers
              (D-106); the table returns at `lg`, where seven columns fit. */}
          <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-2 lg:hidden dark:border-white/10">
            <label
              htmlFor="overview-mobile-sort"
              className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500"
            >
              Sort
            </label>
            <select
              id="overview-mobile-sort"
              value={sort?.key ?? 'shop'}
              onChange={(event) => chooseSortKey(event.target.value as OverviewSortKey)}
              className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 dark:border-white/10 dark:bg-ink-900 dark:text-slate-200"
            >
              {MOBILE_SORT_OPTIONS.filter(
                (option) => showWorked || (option.key !== 'worked' && option.key !== 'diff'),
              ).map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => toggleSort(sort?.key ?? 'shop')}
              aria-label={`Sort ${sort?.direction === 'asc' ? 'descending' : 'ascending'}`}
              title="Switch the sort direction"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-600 dark:border-white/10 dark:text-slate-300"
            >
              {sort?.direction === 'asc' ? (
                <ArrowUp className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ArrowDown className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </div>

          <ul
            aria-label="Shop performance"
            className="divide-y divide-slate-100 lg:hidden dark:divide-white/5"
          >
            {sortedStores.map((row) => (
              <ShopCard key={row.store_id} row={row} showWorked={showWorked} />
            ))}
          </ul>

          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 dark:bg-white/5 dark:text-slate-400">
                <tr>
                  <SortableHeader
                    label="Shop"
                    sortKey="shop"
                    sort={sort}
                    onSort={toggleSort}
                    title="Sort by shop number"
                  />
                  <SortableHeader
                    label="Sales (period)"
                    sortKey="sales"
                    sort={sort}
                    onSort={toggleSort}
                    title="Sort by sales for the period"
                  />
                  <SortableHeader
                    label="Today"
                    sortKey="today"
                    sort={sort}
                    onSort={toggleSort}
                    title="Sort by sales today"
                  />
                  <SortableHeader
                    label="Grid hours"
                    sortKey="grid"
                    sort={sort}
                    onSort={toggleSort}
                    title="Sort by grid hours"
                  />
                  {showWorked && (
                    <SortableHeader
                      label="Worked"
                      sortKey="worked"
                      sort={sort}
                      onSort={toggleSort}
                      title="Sort by actual worked hours"
                    />
                  )}
                  {showWorked && (
                    <SortableHeader
                      label="Grid diff"
                      sortKey="diff"
                      sort={sort}
                      onSort={toggleSort}
                      title="Sort by worked minus grid hours"
                    />
                  )}
                  <th className="px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                {sortedStores.map((row) => {
                  const status = shopStatus(row)
                  const gridDiff =
                    row.worked_hours != null && row.grid_hours != null
                      ? row.worked_hours - row.grid_hours
                      : null
                  return (
                    <tr key={row.store_id}>
                      <td className="px-4 py-2">
                        <span className="font-mono text-[10px] text-slate-400 dark:text-slate-500">
                          #{row.store_code}
                        </span>{' '}
                        <span className="text-slate-700 dark:text-slate-200">
                          {row.store_name}
                        </span>
                      </td>
                      <td className="px-4 py-2 tabular-nums text-slate-700 dark:text-slate-200">
                        {row.sales_period_eur != null
                          ? formatCurrency(row.sales_period_eur)
                          : '—'}
                      </td>
                      <td className="px-4 py-2 tabular-nums text-slate-700 dark:text-slate-200">
                        {row.sales_today_eur != null
                          ? formatCurrency(row.sales_today_eur)
                          : '—'}
                      </td>
                      <td className="px-4 py-2 tabular-nums text-slate-700 dark:text-slate-200">
                        {row.grid_hours != null
                          ? `${formatNumber(row.grid_hours, 1)} h`
                          : '—'}
                      </td>
                      {showWorked && (
                        <td
                          className="px-4 py-2 tabular-nums text-slate-700 dark:text-slate-200"
                          title={row.hours_error_message ?? undefined}
                        >
                          {row.worked_hours != null
                            ? `${formatNumber(row.worked_hours, 1)} h`
                            : row.hours_error_code === 'shiftbase_store_not_configured'
                              ? 'No mapping'
                              : row.hours_error_code
                                ? 'Unavailable'
                                : '—'}
                        </td>
                      )}
                      {showWorked && (
                        <td className="px-4 py-2">
                          {gridDiff != null ? (
                            <span
                              className={`badge ${
                                gridDiff < 0
                                  ? DELTA_TONE.good
                                  : gridDiff > 0
                                    ? DELTA_TONE.bad
                                    : DELTA_TONE.neutral
                              }`}
                              title="Worked minus grid. Green is below grid; red is above grid."
                            >
                              {formatSignedHours(gridDiff)}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                      )}
                      <td className="px-4 py-2">
                        <span className={`badge ${status.tone}`} title={status.title}>
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <p className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-500">
        Butlaroo Insights (vendor aggregate, ex-VAT) · labor grid · business-day figures
        start at 04:00.{showWorked ? ' Worked hours use timesheets for completed days and elapsed roster shifts for today.' : ''}
      </p>
    </section>
  )
}
